/**
 * Main auth landing — hero shell, dock, and guest welcome entry.
 */
import React, { memo, useEffect, useMemo, useState } from 'react';
import { View, Text, Image, StyleSheet, useWindowDimensions, ImageBackground, I18nManager, Platform, type TextStyle } from 'react-native';
import { resolveWebLayoutWidth } from '../shared/platform/webLayout';
import { AppLinearGradient as LinearGradient } from '../components/ui/AppLinearGradient';
import Animated from 'react-native-reanimated';
import { useIsFocused } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuthNavigation, useRedirectAuthenticatedFromGuestWelcome } from '../shared/navigation/useAuthNavigation';
import { appTheme, hexToRgba, useThemeColors, useThemeStore, type ThemeColors } from '../shared/theme';
import {
  createStackedRadialMenuShellStyles,
  HERO_CARD_SHELL_BOTTOM_RADIUS,
  heroShellFrostedGradientColors,
  hero_shell_control_radius,
  type StackedRadialMenuShellStyles,
} from '../shared/theme/styles/welcomeHero';
import { useTranslation, getLocale } from '../shared/i18n';
import { Clickable, PRESS_PRESETS } from '../components/common/Clickable';
import { SvgIcon } from '../components/common/SvgIcon';
import { ErrorState } from '../components/common/ErrorState';
import { fetchInitialConfig, getConfigLoadError, useConsultationsFeatureEnabled, useGuestFlowType, useGuestMenuListMode, useJourneyFeatureEnabled, useOnlineOrdersFeatureEnabled } from '../data/domains/config';
import PattMainSvg from '../assets/images/onboarding/pattmain.svg';

import ClientLogoSvg from '../shared/brand/clientLogo';
import { useMainScreenDockBackdropStyle, useMainScreenOnboardingMotion } from '../shared/theme/studio/onboardingMotion';
import { MAIN_SCREEN_AUTH_BAR, MAIN_SCREEN_DOCK, MAIN_SCREEN_HERO } from '../shared/auth/onboardingMainScreenConfig';
import { MainScreenDockLayer } from './mainScreenDockLayer';
import { useMainScreenDockController } from './useMainScreenDockController';
import { DevHubGuestDevToolsEntry } from '../exDev';
import {
  androidStripElevationSeams,
  androidScaleSize,
  glassAccentBorderStyle,
} from '../shared/theme/sharedStyles';
import {
  androidMainScreenAuthBarStyle,
  androidMainScreenHealthPillStyle,
  androidMainScreenHeroColumnLayoutStyle,
  androidMainScreenLangPillStyle,
  androidMainScreenChromeTopPadding,
  isMainScreenAndroid,
  mainScreenHeroColumnStackStyle,
} from '../shared/auth/onboardingMainScreenAndroidParity';
// import { BottomSheetPopup } from '../components/common/BottomSheetPopup';
import { BottomSheetPopup } from '../components/common/BottomSheetPopup';
import { HealthInformationPopupScrollBody } from '../healthInformation/components/HealthInformationScrollBody';
import { useHealthInformationCms } from '../healthInformation/components/HealthInformationCmsContext';
import {
  filterMainScreenMenuRoutes,
  MAIN_SCREEN_MENU_ROUTES,
  resolveGuestRouteParams,
  shouldShowMainScreenConsultation,
  shouldShowMainScreenJourney,
} from '../shared/navigation/guestMainScreenNav';

const IS_ANDROID_MAIN = isMainScreenAndroid();

const HeroFrostedControlBackdrop = React.memo(({ colors }: { colors: ThemeColors }) => (
  <LinearGradient
    colors={heroShellFrostedGradientColors(colors)}
    style={StyleSheet.absoluteFillObject}
    pointerEvents="none"
    start={{ x: 0, y: 0 }}
    end={{ x: 0, y: 1 }}
  />
));

function borderBoxOverlay(
  color: string = 'transparent',
  borderWidth: number = 1,
  borderRadius?: number,
  borderTopLeftRadius?: number,
  borderTopRightRadius?: number,
  borderBottomLeftRadius?: number,
  borderBottomRightRadius?: number,
) {
  return (
    <View
      style={[
        StyleSheet.absoluteFillObject,
        {
          borderColor: color,
          borderWidth: borderWidth,
          borderRadius: borderRadius ?? borderRadius,
          borderTopLeftRadius: borderTopLeftRadius ?? borderTopLeftRadius,
          borderTopRightRadius: borderTopRightRadius ?? borderTopRightRadius,
          borderBottomLeftRadius: borderBottomLeftRadius ?? borderBottomLeftRadius,
          borderBottomRightRadius: borderBottomRightRadius ?? borderBottomRightRadius,
          zIndex: 1000,
        },
      ]}
      pointerEvents="none"
    />
  );
}
const M_BG_TILE = require('../assets/images/onboarding/mBgfull.png');
const ORDER_PNG = require('../assets/images/onboarding/order.png');
const MENU_PNG = require('../assets/images/onboarding/menu.png');
const PLAN_PNG = require('../assets/images/onboarding/plan.png');

function mainScreenLogoDims(ww: number) {
  const logoSize = Math.min(Math.max(72, Math.round(ww * 0.75)), 256);
  return { logoW: logoSize, logoH: logoSize };
}

const MENU_PILL_ROUTES = [
  { route: 'Order' as const, icon: ORDER_PNG, labelKey: 'main.order' as const, requires_online_orders: true },
  { route: 'DailyMenuFlow' as const, icon: MENU_PNG, labelKey: 'main.menu' as const },
  { route: 'SubscriptionList' as const, icon: PLAN_PNG, labelKey: 'main.plans' as const },
];

const MainScreenHeroDock = () => {
  const navigation = useAuthNavigation();
  useRedirectAuthenticatedFromGuestWelcome();
  const is_focused = useIsFocused();
  const insets = useSafeAreaInsets();
  const { width: window_w, height: wh } = useWindowDimensions();
  const ww = resolveWebLayoutWidth(window_w);
  const colors = useThemeColors() ?? appTheme.colors;
  const appearance = useThemeStore(s => s.appearance);
  const is_dark = appearance === 'dark';

  /** Health-info launcher beside language switch — icon only, no pill background. */
  const health_info_icon_color = useMemo(
    () => hexToRgba(colors.danger, is_dark ? 0.93 : 0.86),
    [colors.danger, is_dark],
  );

  const styles = useMemo(() => {
    const base = createStackedRadialMenuShellStyles(colors);
    const label_base = base.bottomAuthLabel;
    const { flex: _fl, minWidth: _mw, ...label_rest } = label_base;
    const auth_label_android =
      Platform.OS === 'android' ? { includeFontPadding: false, textAlignVertical: 'center' as const } : null;
    return {
      ...base,
      bottomAuthLabel: {
        ...label_rest,
        width: '100%',
        textAlign: 'center',
        color: colors.primaryDark,
        fontSize: Platform.OS === 'android' ? androidScaleSize(12) : 15,
        lineHeight: Platform.OS === 'android' ? androidScaleSize(14) : 20,
        fontWeight: Platform.OS === 'android' ? '500' : '600',
        letterSpacing: Platform.OS === 'android' ? androidScaleSize(0.4) : 0.4,
        textTransform: 'capitalize',
        ...(auth_label_android ?? {}),
      } satisfies TextStyle,  
    };
  }, [colors]);
  const { t, setLocale } = useTranslation();
  const show_consultation_entry = useConsultationsFeatureEnabled();
  const is_journey = useJourneyFeatureEnabled();
  const show_online_orders = useOnlineOrdersFeatureEnabled();
  const guest_flow_type = useGuestFlowType();
  const guest_menu_list = useGuestMenuListMode();

  const menu_pill_routes = useMemo(
    () =>
      filterMainScreenMenuRoutes(
        MAIN_SCREEN_MENU_ROUTES.map(route => ({
          ...route,
        })),
        guest_menu_list,
        show_online_orders,
      ).map(route => MENU_PILL_ROUTES.find(item => item.route === route.route)).filter(Boolean) as typeof MENU_PILL_ROUTES,
    [guest_menu_list, show_online_orders],
  );

  const [app_config_error_message, setAppConfigErrorMessage] = useState<string | null>(getConfigLoadError);
  const [health_info_sheet_open, setHealthInfoSheetOpen] = useState(false);
  const { isAvailable: health_info_available, pageTitle: health_info_page_title } = useHealthInformationCms();

  const dims = useMemo(() => mainScreenLogoDims(ww), [ww]);

  const motion = useMainScreenOnboardingMotion(wh);
  const {
    applyMainPhase,
    authFormSwap,
    dockOpen,
    dockExit,
    card_enter_style,
    bottom_enter_style,
    patt_animated_style,
    lang_style,
    logo_style,
    menu_style,
    journey_style,
    consult_style,
    login_label_style,
    login_chevron_style,
    create_label_style,
    create_chevron_style,
    blur_style,
  } = motion;

  // const dock_backdrop_a = useMainScreenDockBackdropStyle(dockOpen);

  const dock = useMainScreenDockController(applyMainPhase, insets.bottom);
  const {
    main_dock,
    sheet_phase,
    is_idle_sheet,
    android_dock_active,
    dock_backdrop_visible,
    auth_float_active,
    form_mounted,
    android_dismiss_reserve_bottom,
    onDismissMainAuth,
    openMainDock,
    switchToLogin,
    switchToRegister,
  } = dock;

  const main_screen_form_scroll_max = useMemo(
    () => Math.max(160, Math.round(wh * MAIN_SCREEN_HERO.cardHeightFraction) - MAIN_SCREEN_DOCK.sheetChromeH),
    [wh],
  );
  useEffect(() => {
    setAppConfigErrorMessage(getConfigLoadError());
  }, []);

  const handleRetryConfigLoad = async () => {
    setAppConfigErrorMessage(null);
    await fetchInitialConfig();
    setAppConfigErrorMessage(getConfigLoadError());
  };

  const toggle_language = () => setLocale(getLocale() === 'en' ? 'ar' : 'en');
  const lang_switch_label = getLocale() === 'en' ? t('main.switchToArabic') : t('main.switchToEnglish');

  const card_shell_style = useMemo(
    () => [
      styles.cardInner,
      {
        paddingTop: IS_ANDROID_MAIN
          ? androidMainScreenChromeTopPadding(insets.top)
          : insets.top + appTheme.spacing.md + appTheme.spacing.xs,
        height: wh * MAIN_SCREEN_HERO.cardHeightFraction,
        marginTop: wh * MAIN_SCREEN_HERO.cardTopOffsetFraction,
      },
      is_idle_sheet && styles.cardIn2,
      IS_ANDROID_MAIN ? styles.cardIn2 : null,
    ],
    [styles.cardInner, insets.top, wh, is_idle_sheet],
  );

  const shadow_style = {
    elevation: 2,
    shadowColor: appTheme.colors.black,
    shadowOpacity: 0,
    shadowRadius: 0,
    shadowOffset: { width: 0, height: 0 },
  }
  const action_row_shadow = IS_ANDROID_MAIN ? { ...androidStripElevationSeams(), ...shadow_style } : appTheme.shadows.xl;

  if (app_config_error_message) {
    return (
      <View style={styles.root}>
        <ErrorState
          title={t('common.error')}
          message={app_config_error_message}
          onRetry={handleRetryConfigLoad}
          issue_context={{ page: 'Main Screen', issue: 'Initial configuration failed to load', kind: 'api', api_endpoint: 'initial', http_method: 'GET' }}
        />
      </View>
    );
  }

  return (
    <View style={styles.root}>
      <ImageBackground source={M_BG_TILE} style={styles.bgTile} resizeMode="cover" />

      {/* {IS_ANDROID_MAIN && dock_backdrop_visible ? (
        <Animated.View
          pointerEvents="none"
          style={[androidMainScreenDockBackdropLayerStyle(), { backgroundColor: colors.backdrop }, dock_backdrop_a]}
        />
      ) : null} */}

      <View
        style={[
          styles.heroColumn,
          IS_ANDROID_MAIN ? androidMainScreenHeroColumnLayoutStyle() : null,
          mainScreenHeroColumnStackStyle(auth_float_active),
          { pointerEvents: 'box-none' },
        ]}
        collapsable={false}>
        <Animated.View style={[card_shell_style, card_enter_style]}>
          <LinearGradient colors={[colors.tertiaryDark, colors.primary]} locations={[0, 1]} style={[styles.cardRadialLayer, { pointerEvents: 'none' }]} start={{ x: 0, y: 0.5 }} end={{ x: 0, y: 1 }} />

          <Animated.View style={[StyleSheet.absoluteFill, patt_animated_style, { pointerEvents: 'none' }]}>
            <PattMainSvg width="100%" height="100%" style={styles.pattOverlay} />
          </Animated.View>

          <Animated.View style={[styles.langSectionOuter, lang_style]}>
            <View
              style={{
                flexDirection: IS_ANDROID_MAIN ? 'row-reverse' : 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                gap: appTheme.spacing.sm,
              }}>
              <Clickable
                onPress={toggle_language}
                style={[
                  styles.langBtn,
                  IS_ANDROID_MAIN
                    ? androidMainScreenLangPillStyle
                    : { flex: 1, maxWidth: '78%' },
                ]}
                contentStyle={styles.shellClickableInnerFillCenter}
                accessibilityLabel={lang_switch_label}
                pressScale={PRESS_PRESETS.listRow.scale}
                pressSpring={PRESS_PRESETS.listRow.spring}
                useNativePressable={IS_ANDROID_MAIN}
                haptics>
                <Text numberOfLines={1} style={styles.langBtnText}>{lang_switch_label}</Text>
              </Clickable>
              <DevHubGuestDevToolsEntry />
              <Clickable
                onPress={() => {
                  if (health_info_available) {
                    setHealthInfoSheetOpen(true);
                  }
                }}
                style={[
                  IS_ANDROID_MAIN ? androidMainScreenHealthPillStyle : { width: 48, minWidth: 48, maxWidth: 48, flexShrink: 0 },
                  {
                    backgroundColor: 'transparent',
                    borderWidth: 0,
                    justifyContent: 'center',
                    alignItems: 'center',
                  },
                ]}
                contentStyle={styles.shellClickableInnerFillCenter}
                accessibilityLabel={t('healthInformation.openInfoAccessibility')}
                pressScale={PRESS_PRESETS.listRow.scale}
                pressSpring={PRESS_PRESETS.listRow.spring}
                useNativePressable={IS_ANDROID_MAIN}
                haptics>
                <SvgIcon
                  name="health-alert"
                  size={IS_ANDROID_MAIN ? androidScaleSize(24) : 32}
                  color={health_info_icon_color}
                  style={{ pointerEvents: 'none' }}
                />
              </Clickable>
            </View>
          </Animated.View>

          <Animated.View style={[styles.logoSection, logo_style]}>
            <View style={styles.logoBlock}>
              <ClientLogoSvg
                width={IS_ANDROID_MAIN ? androidScaleSize(dims.logoW) : dims.logoW}
                height={IS_ANDROID_MAIN ? androidScaleSize(dims.logoH) : dims.logoH}
                color={colors.secondary}
              />
            </View>
          </Animated.View>

          <View style={styles.cardBottomCluster}>
            <Animated.View style={[styles.menuShortcutRow, menu_style]} pointerEvents={is_idle_sheet ? 'auto' : 'none'}>
              {menu_pill_routes.map(item => (
                <View
                  key={item.route}
                  style={styles.menuShortcutSlot}
                  collapsable={IS_ANDROID_MAIN ? false : undefined}
                  pointerEvents="box-none">
                  <View style={styles.menuShortcutImageWrap} pointerEvents="none">
                    <Image source={item.icon} style={styles.menuShortcutImage} resizeMode="contain" />
                  </View>
              
                  <Clickable
                    style={styles.menuShortcutBtn}
                    contentStyle={styles.shellClickableInnerMenuPill}
                    onPress={() =>
                      (navigation as { navigate: (r: string, p: object) => void }).navigate(
                        item.route,
                        resolveGuestRouteParams(item.route, guest_flow_type),
                      )
                    }
                    pressScale={PRESS_PRESETS.listRow.scale}
                    pressSpring={PRESS_PRESETS.listRow.spring}
                    useNativePressable={IS_ANDROID_MAIN}
                    haptics>
                    <View
                      style={[
                        styles.shellClickableInnerMenuPill,
                        {
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          overflow: 'hidden',
                          borderRadius: appTheme.borderRadius.lg,
                        },
                      ]}
                      pointerEvents="none">
                      <HeroFrostedControlBackdrop colors={colors} />
                    </View>
                    <Text style={styles.menuShortcutLabel} numberOfLines={2}>
                      {t(item.labelKey)}
                    </Text>
                  </Clickable>
                </View>
              ))}
            </Animated.View>

            {shouldShowMainScreenJourney(guest_menu_list, is_journey) ? (
            <Animated.View style={[journey_style,  { zIndex: 13 }]}>
              <Clickable
                style={[
                  styles.journeyRow,
                  // action_row_shadow,
                  // IS_ANDROID_MAIN
                  //   ? glassAccentBorderStyle(colors.secondary, {
                  //       borderTopLeftRadius: hero_shell_control_radius,
                  //       borderTopRightRadius: hero_shell_control_radius,
                  //       borderBottomLeftRadius: hero_shell_control_radius,
                  //       borderBottomRightRadius: hero_shell_control_radius,
                  //     })
                  //   : null,
                ]}
                contentStyle={[styles.shellClickableInnerActionRow]}
                onPress={() => navigation.navigate('Journey', { from: 'main' })}
                pressScale={PRESS_PRESETS.listRow.scale}
                useNativePressable={IS_ANDROID_MAIN}
                haptics>
                <HeroFrostedControlBackdrop colors={colors} />
                {/* {!IS_ANDROID_MAIN
                  ? borderBoxOverlay(
                      colors.secondary,
                      1,
                      appTheme.borderRadius.lg,
                      hero_shell_control_radius,
                      hero_shell_control_radius,
                      hero_shell_control_radius,
                      hero_shell_control_radius,
                    )
                  : null} */}
                <View style={styles.actionRowContent}>
                  <View style={[styles.actionRowIconBadge, styles.trailing]}>
                    <SvgIcon name="health-quiz-deco" size={appTheme.sizes.buttonHeightLarge + appTheme.spacing.lg + appTheme.spacing.lg} color={colors.primaryDark} />
                  </View>
                  <Text style={[styles.actionRowLabelLeading, { textAlign: 'left' }]} numberOfLines={2}>
                    {t('main.takeHealthQuiz')}
                  </Text>
                </View>
              </Clickable>
            </Animated.View>
            ) : null}

            {shouldShowMainScreenConsultation(guest_menu_list, show_consultation_entry) ? (
            <Animated.View style={[consult_style, { zIndex: 14 }]}>
              <Clickable
                style={[
                  styles.consultRow,
                  
                  // IS_ANDROID_MAIN
                  //   ? glassAccentBorderStyle(colors.secondaryHalf, {
                  //       borderTopLeftRadius: hero_shell_control_radius,
                  //       borderTopRightRadius: hero_shell_control_radius,
                  //       borderBottomLeftRadius: HERO_CARD_SHELL_BOTTOM_RADIUS,
                  //       borderBottomRightRadius: HERO_CARD_SHELL_BOTTOM_RADIUS,
                  //     })
                  //   : null,
                ]}
                contentStyle={[styles.shellClickableInnerActionRow, { borderBottomLeftRadius: HERO_CARD_SHELL_BOTTOM_RADIUS, borderBottomRightRadius: HERO_CARD_SHELL_BOTTOM_RADIUS }]}
                onPress={() => navigation.navigate('Consultation')}
                pressScale={PRESS_PRESETS.listRow.scale}
                useNativePressable={IS_ANDROID_MAIN}
                haptics>
                <HeroFrostedControlBackdrop colors={colors} />
                {/* {!IS_ANDROID_MAIN
                  ? borderBoxOverlay(
                      colors.secondaryHalf,
                      1,
                      appTheme.borderRadius.lg,
                      hero_shell_control_radius,
                      hero_shell_control_radius,
                      HERO_CARD_SHELL_BOTTOM_RADIUS,
                      HERO_CARD_SHELL_BOTTOM_RADIUS,
                    )
                  : null} */}
                <View style={styles.actionRowContent}>
                  <View style={[styles.actionRowIconBadge, styles.leading]}>
                    <SvgIcon name="consult-expert-deco" size={appTheme.sizes.buttonHeightLarge + appTheme.spacing.lg + appTheme.spacing.lg} color={colors.primaryDark} />
                  </View>
                  <Text style={[styles.actionRowLabelTrailing, { textAlign: 'right' }]} numberOfLines={2}>
                    {t('main.talkToExpert')}
                  </Text>
                </View>
              </Clickable>
            </Animated.View>
            ) : null}
          </View>
        </Animated.View>
      </View>

      <MainScreenDockLayer
        colors={colors}
        wh={wh}
        ww={ww}
        insets_bottom={insets.bottom}
        main_dock={main_dock}
        auth_float_active={auth_float_active}
        dock_backdrop_visible={dock_backdrop_visible}
        render_backdrop
        form_mounted={form_mounted}
        form_scroll_max={main_screen_form_scroll_max}
        dismiss_label={t('common.close')}
        dismiss_reserve_bottom={android_dismiss_reserve_bottom}
        dockOpen={dockOpen}
        dockExit={dockExit}
        authFormSwap={authFormSwap}
        onDismiss={onDismissMainAuth}
        onSwitchToRegister={switchToRegister}
        onSwitchToLogin={switchToLogin}
      />

      <Animated.View
        style={[
          styles.bottomAuthBar,
          { marginBottom: insets.bottom + MAIN_SCREEN_AUTH_BAR.marginBottom },
          androidMainScreenAuthBarStyle(android_dock_active),
          bottom_enter_style,
        ]}>
        <AuthChip
          label={t('auth.signIn')}
          onPress={() => {
            if (sheet_phase === 'login') {
              onDismissMainAuth();
            } else {
              openMainDock(1);
            }
          }}
          labelStyle={login_label_style}
          chevronStyle={login_chevron_style}
          primaryDark={colors.primaryDark}
          styles={styles}
        />
        <AuthChip
          label={t('auth.createAccount')}
          onPress={() => {
            if (sheet_phase === 'create') {
              onDismissMainAuth();
            } else {
              openMainDock(2);
            }
          }}
          labelStyle={create_label_style}
          chevronStyle={create_chevron_style}
          primaryDark={colors.primaryDark}
          styles={styles}
        />
      </Animated.View>

      <View style={styles.merakFooter} pointerEvents="none">
        <Text style={styles.merakFooterText}>{t('main.poweredByMerak')}</Text>
      </View>

      {health_info_available ? (
        <BottomSheetPopup
          visible={health_info_sheet_open}
          onClose={() => setHealthInfoSheetOpen(false)}
          title={health_info_page_title || t('healthInformation.title')}
          showDragHandle
          animationType="slide">
          <HealthInformationPopupScrollBody />
        </BottomSheetPopup>
      ) : null}
    </View>
  );
};

type AuthChipShellStyles = Pick<
  StackedRadialMenuShellStyles,
  'bottomAuthBtn' | 'shellClickableInnerAuthChip' | 'bottomAuthStack' | 'bottomAuthBtnChevron'
> & { bottomAuthLabel: TextStyle };

type AuthChipProps = {
  label: string;
  onPress: () => void;
  labelStyle: object;
  chevronStyle: object;
  primaryDark: string;
  styles: AuthChipShellStyles;
};

const AuthChip = memo(function AuthChip({ label, onPress, labelStyle, chevronStyle, primaryDark, styles }: AuthChipProps) {
  const chevron_size = appTheme.sizes.smallIcon;
  return (
    <Clickable
      style={styles.bottomAuthBtn}
      contentStyle={styles.shellClickableInnerAuthChip}
      onPress={onPress}
      useNativePressable={IS_ANDROID_MAIN}
      pressScale={PRESS_PRESETS.button.scale}
      haptics>
      <View style={styles.bottomAuthStack}>
        <Animated.Text style={[styles.bottomAuthLabel, labelStyle]} numberOfLines={1}>
          {label}
        </Animated.Text>
        <Animated.View style={[styles.bottomAuthBtnChevron, chevronStyle]} pointerEvents="none">
          <SvgIcon name="chevron-down" size={chevron_size} color={primaryDark} style={{ pointerEvents: 'none' }} />
        </Animated.View>
      </View>
    </Clickable>
  );
});

export default MainScreenHeroDock;
